import socket from "@/services/socket";
import {
  useGrimoireStore,
  useLocaleStore,
  usePlayersStore,
  useRochamboStore,
  useSessionStore,
  useUserPreferencesStore,
  useVotingStore,
} from "@/stores";
import type {
  GamePhase,
  Nomination,
  RochamboMatch,
  VoteHistoryEntry,
} from "@/types";
export async function updateGameState(data: Record<string, unknown>) {
  const session = useSessionStore();
  if (!session.isPlayerOrSpectator) return;
  const grimoireStore = useGrimoireStore();
  const playersStore = usePlayersStore();
  const votingStore = useVotingStore();
  const rochamboStore = useRochamboStore();
  const userPreferences = useUserPreferencesStore();
  const localeStore = useLocaleStore();

  const {
    gamestate,
    isLightweight,
    gamePhase,
    dayCount,
    allowSelfNaming,
    isVoteHistoryAllowed,
    isSecretVoteMode,
    isSignalHistoryAllowed,
    isTextChatAllowed,
    timer,
    nomination,
    votingSpeed,
    votes,
    voteHistory,
    lockedVote,
    isVoteInProgress,
    markedPlayer,
    fabled,
    rochambo,
    locale,
  } = data as {
    gamestate: Array<{
      name: string;
      id: string;
      isDead: boolean;
      voteToken: boolean;
      pronouns: string;
      roleId?: string;
      discordTag?: string;
      handRaisedAt?: number | null;
    }>;
    isLightweight?: boolean;
    gamePhase?: GamePhase;
    dayCount?: number;
    allowSelfNaming?: boolean;
    isVoteHistoryAllowed?: boolean;
    isSecretVoteMode?: boolean;
    isTextChatAllowed?: boolean;
    isSignalHistoryAllowed?: boolean;
    timer?: { name?: string; duration?: number };
    nomination: Nomination | null;
    votingSpeed?: number;
    votes?: number[];
    voteHistory?: VoteHistoryEntry[];
    lockedVote?: number;
    isVoteInProgress?: boolean;
    markedPlayer?: number;
    fabled: Array<{ id: string; isCustom?: boolean }>;
    rochambo?: RochamboMatch | null;
    locale: string;
  };

  await localeStore.forceLocale(locale);

  const players = playersStore.players;
    // adjust number of players
    if (gamestate.length < session.claimedSeat) {
      session.claimSeat(-1);
    }
    if (players.length < gamestate.length) {
      for (let x = players.length; x < gamestate.length; x++) {
        playersStore.add(String(gamestate[x]?.name ?? ""), String(gamestate[x]?.discordTag ?? ""));
        if (String(gamestate[x]?.discordTag ?? "") === userPreferences.discordName) {
          socket._autoClaimSeatByDiscordTag();
        }
      }
    } else if (players.length > gamestate.length) {
      for (let x = players.length; x > gamestate.length; x--) {
        playersStore.remove(x - 1);
      }
    }
  // update status for each player
  gamestate.forEach((st, x) => {
    // Get fresh reference to players array after potential additions/removals
    const currentPlayers = playersStore.players;
    const player = currentPlayers[x];
    const { roleId } = st;
    (["name", "id", "isDead", "voteToken", "pronouns", "discordTag"] as const).forEach(
      (property) => {
        const value = st[property];
        if (
          player &&
          player[property] !== value
        ) {
          playersStore.update({ player, property, value, isFromSocket: true});
        }
      }
    );
    const handRaisedAt = st.handRaisedAt ?? null;
    if (player && player.handRaisedAt !== handRaisedAt) {
      playersStore.update({
        player,
        property: "handRaisedAt",
        value: handRaisedAt,
        isFromSocket: true,
      });
    }
    if (player && roleId && player.role?.id !== roleId) {
      const role =
        grimoireStore.roles.get(roleId) ||
        grimoireStore.rolesJSONbyId.get(roleId);
      if (role)
        playersStore.update({
          player,
          property: "role",
          value: role,
        });
    } else if (player && !roleId && player.role?.team === "traveler") {
      playersStore.update({
        player,
        property: "role",
        value: {},
      });
    }
  });
  if (!isLightweight) {
    grimoireStore.setTimer(timer || {});
    grimoireStore.setAllowSelfNaming(!!allowSelfNaming);
    grimoireStore.setVoteHistoryAllowed(!!isVoteHistoryAllowed);
    grimoireStore.setSecretVote(!!isSecretVoteMode);
    grimoireStore.setAllowTextChat(!!isTextChatAllowed);
    grimoireStore.setAllowSignalHistory(!!isSignalHistoryAllowed);
    votingStore.updateNomination({
      nomination,
      votes: votes || [],
      votingSpeed: votingSpeed || 0,
      lockedVote: lockedVote || -1,
      isVoteInProgress: isVoteInProgress || false,
    });
    votingStore.setVoteHistory(voteHistory || []);
    votingStore.setMarkedPlayer(markedPlayer || -1);
    grimoireStore.setGamePhase(gamePhase || "pregame");
    grimoireStore.setDayCount(dayCount || 0);
    playersStore.setFabled({
      fabled: fabled.map((f) => grimoireStore.fabled.get(f.id) || f),
    });
    rochamboStore.setMatch(rochambo ?? null);
  }
}
